import { useState } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Plus, Trash2, ChevronDown, Loader2, AlertCircle } from "lucide-react";
import { toast } from "sonner";
import {
  addRole,
  updateRole,
  deleteRole,
  toggleRoleIncluded,
} from "@/hooks/payrollDataService";

interface AdvancedModeProps {
  propertyId: string;
  entries: PayrollEntry[];
  onEntriesChange: (entries: PayrollEntry[]) => void;
  totalMonthlyCost: number;
  totalAnnualCost: number;
  suggestedRoles?: SuggestedRole[];
  onAcceptSuggestion?: (roleId: string) => void;
  socialChargesPercent: number;
  onSocialChargesChange?: (value: number) => void;
}

const MAX_ROLES_PER_DEPARTMENT = 7;

const DEPARTMENTS = [
  "Direct Rooms Payroll",
  "F&B Direct Payroll",
  "Wellness Direct Payroll",
  "A&G Payroll",
  "Sales & Marketing Payroll",
  "Maintenance Payroll",
];

const DEPARTMENT_DISPLAY_MAP: Record<string, string> = {
  "Front Office": "Direct Rooms Payroll",
  Housekeeping: "Direct Rooms Payroll",
  "Food & Beverage": "F&B Direct Payroll",
  "Spa & Wellness": "Wellness Direct Payroll",
  Spa: "Wellness Direct Payroll",
  Wellness: "Wellness Direct Payroll",
  Management: "A&G Payroll",
  "Admin & General": "Sales & Marketing Payroll",
  Administration: "A&G Payroll",
  "Sales & Marketing": "Sales & Marketing Payroll",
  Maintenance: "Maintenance Payroll",
  Engineering: "Maintenance Payroll",
  Other: "A&G Payroll",
  "Direct Rooms Payroll": "Direct Rooms Payroll",
  "F&B Direct Payroll": "F&B Direct Payroll",
  "Wellness Direct Payroll": "Wellness Direct Payroll",
  "A&G Payroll": "A&G Payroll",
  "Sales & Marketing Payroll": "Sales & Marketing Payroll",
  "Maintenance Payroll": "Maintenance Payroll",
};

const ROLE_LIBRARY: Record<string, string[]> = {
  "Direct Rooms Payroll": [
    "Front Office Manager",
    "Front Desk Agent",
    "Concierge",
    "Night Auditor",
    "Bellhop / Porter",
    "Housekeeping Manager",
    "Room Attendant",
    "Housekeeping Supervisor",
    "Laundry Attendant",
    "Public Area Cleaner",
  ],
  "F&B Direct Payroll": [
    "F&B Manager",
    "Restaurant Manager",
    "Server / Waiter",
    "Bartender",
    "Chef",
    "Cook",
    "Barista",
    "Breakfast Attendant",
  ],
  "Wellness Direct Payroll": [
    "Spa Manager",
    "Spa Therapist",
    "Massage Therapist",
    "Beautician",
    "Fitness Instructor",
  ],
  "A&G Payroll": [
    "General Manager",
    "Assistant General Manager",
    "Operations Manager",
    "Revenue Manager",
    "Accountant",
    "HR Manager",
    "IT Support",
  ],
  "Sales & Marketing Payroll": [
    "Sales Manager",
    "Marketing Manager",
    "PR Manager",
    "Reservations Manager",
    "E-Commerce Manager",
  ],
  "Maintenance Payroll": [
    "Chief Engineer",
    "Maintenance Technician",
    "Handyman",
    "Pool Technician",
    "Electrician",
  ],
};

const DEPARTMENT_IDS: Record<string, string> = {
  "Direct Rooms Payroll": "0d04c044-87bd-4d24-9007-f0628ef6ee3a",
  "F&B Direct Payroll": "d93b774f-3906-47e9-ba2f-90675588f31c",
  "Wellness Direct Payroll": "559c7da5-01f0-4a2d-9764-ad7d6fb70220",
  "A&G Payroll": "508d5c3b-c493-43f9-a796-11558a7a1fc9",
  "Sales & Marketing Payroll": "8607eb3b-2103-4615-b96c-9eef9f282afa", 
  "Maintenance Payroll": "c5e5738e-0b31-4940-9b10-8ca654b9917f",
};

function normalizeDept(department: string): string {
  return DEPARTMENT_DISPLAY_MAP[department] || department;
}

export const AdvancedMode = ({
  propertyId,
  entries,
  onEntriesChange,
  totalMonthlyCost,
  totalAnnualCost,
  suggestedRoles = [],
  onAcceptSuggestion,
  socialChargesPercent,
}: AdvancedModeProps) => {
  const [loadingIds, setLoadingIds] = useState<Set<string>>(new Set());
  const [errorIds, setErrorIds] = useState<Set<string>>(new Set());

  const setLoading = (id: string, value: boolean) =>
    setLoadingIds((prev) => {
      const next = new Set(prev);
      value ? next.add(id) : next.delete(id);
      return next;
    });

  const setError = (id: string, value: boolean) =>
    setErrorIds((prev) => {
      const next = new Set(prev);
      value ? next.add(id) : next.delete(id);
      return next;
    });

  const grandTotalFtes = entries.reduce((sum, entry) => sum + entry.ftes, 0);

  const handleAddEntry = async (department: string, roleName?: string) => {
    const normalizedDept = normalizeDept(department);
    const deptCount = entries.filter(
      (e) => normalizeDept(e.department) === normalizedDept,
    ).length;

    if (deptCount >= MAX_ROLES_PER_DEPARTMENT) {
      toast.error(
        `Maximum ${MAX_ROLES_PER_DEPARTMENT} roles allowed per department`,
      );
      return;
    }

    const salary = 2500;
    const ftes = 1;
    const totalCost = salary * ftes * (1 + socialChargesPercent / 100);

    const tempId = crypto.randomUUID();
    const optimisticEntry: PayrollEntry = {
      id: tempId,
      department: normalizedDept,
      role: roleName || "New Role",
      ftes,
      salary,
      socialCharges: socialChargesPercent,
      totalCost,
    };
    onEntriesChange([...entries, optimisticEntry]);
    setLoading(tempId, true);

    try {
      const result = await addRole(propertyId, {
        positionName: roleName || "New Role",
        departmentId: DEPARTMENT_IDS[normalizedDept] || "",
        included: true,
        calculatedFTEs: ftes,
        monthlySalary: salary,
        overrideFTEs: null,
        overrideSalary: null,
      });

      const savedPosition = result.data.positions.at(-1);
      if (savedPosition) {
        onEntriesChange([
          ...entries,
          { ...optimisticEntry, id: savedPosition.positionId },
        ]);
      }

      toast.success(`${roleName || "New role"} added to ${normalizedDept}`);
    } catch (err) {
      onEntriesChange(entries);
      setError(tempId, true);
      toast.error("Failed to add role. Please try again.");
    } finally {
      setLoading(tempId, false);
    }
  };

  const handleDeleteEntry = async (id: string) => {
    const previousEntries = [...entries];
    onEntriesChange(entries.filter((e) => e.id !== id));
    setLoading(id, true);

    try {
      await deleteRole(propertyId, id);
      toast.success("Role deleted successfully");
    } catch (err) {
      onEntriesChange(previousEntries);
      toast.error("Failed to delete role. Please try again.");
    } finally {
      setLoading(id, false);
    }
  };

  const handleUpdateEntry = (
    id: string,
    field: keyof PayrollEntry,
    value: string | number,
  ) => {
    const updatedEntries = entries.map((entry) => {
      if (entry.id !== id) return entry;
      const updated = { ...entry, [field]: value };
      if (field === "salary" || field === "ftes") {
        const s = field === "salary" ? Number(value) : entry.salary;
        const f = field === "ftes" ? Number(value) : entry.ftes;
        updated.totalCost = s * f * (1 + socialChargesPercent / 100);
      }
      return updated;
    });
    onEntriesChange(updatedEntries);
  };

  const handleBlurEntry = async (id: string) => {
    const entry = entries.find((e) => e.id === id);
    if (!entry) return;

    setLoading(id, true);
    setError(id, false);

    try {
      await updateRole(propertyId, id, {
        positionName: entry.role,
        monthlySalary: entry.salary,
        calculatedFTEs: entry.ftes,
        departmentId: DEPARTMENT_IDS[normalizeDept(entry.department)] || "",
        included: true,
        overrideFTEs: null,
        overrideSalary: null,
      });
    } catch (err) {
      setError(id, true);
      toast.error(`Failed to save "${entry.role}"`);
    } finally {
      setLoading(id, false);
    }
  };

  const handleAcceptSuggestion = async (roleId: string) => {
    if (!onAcceptSuggestion) return;

    setLoading(roleId, true);
    try {
      await toggleRoleIncluded(propertyId, roleId);
      onAcceptSuggestion(roleId);
      toast.success("Role added successfully");
    } catch (err) {
      toast.error("Failed to add role. Please try again.");
    } finally {
      setLoading(roleId, false);
    }
  };

  const entriesByDepartment = entries.reduce(
    (acc, entry) => {
      const dept = normalizeDept(entry.department);
      if (!acc[dept]) acc[dept] = [];
      acc[dept].push(entry);
      return acc;
    },
    {} as Record<string, PayrollEntry[]>,
  );

  const suggestionsByDepartment = suggestedRoles.reduce(
    (acc, role) => {
      const dept = normalizeDept(role.department || "A&G Payroll");
      if (!acc[dept]) acc[dept] = [];
      acc[dept].push(role);
      return acc;
    },
    {} as Record<string, SuggestedRole[]>,
  );

  const calculateDepartmentTotals = (deptEntries: PayrollEntry[]) => {
    const totalFtes = deptEntries.reduce((sum, e) => sum + e.ftes, 0);
    const totalAnnual = deptEntries.reduce(
      (sum, e) => sum + e.totalCost * 12,
      0,
    );
    return { totalFtes, totalAnnual };
  };

  const allDepartments = DEPARTMENTS;

  return (
    <div className="space-y-6">
      {allDepartments.map((department) => {
        const deptEntries = entriesByDepartment[department] || [];
        const deptSuggestions = suggestionsByDepartment[department] || [];
        const { totalFtes, totalAnnual } =
          calculateDepartmentTotals(deptEntries);
        const libraryRoles = ROLE_LIBRARY[department] || ["Custom Role"];
        const isAtLimit = deptEntries.length >= MAX_ROLES_PER_DEPARTMENT;


        return (
          <Card key={department} className="overflow-hidden">
            <div className="p-4 flex items-center justify-between border-b bg-background">
              <div className="flex items-center gap-3">
                <h3 className="font-semibold text-foreground">{department}</h3>
                <span
                  className={`text-xs px-2 py-0.5 rounded-full font-medium ${
                    isAtLimit
                      ? "bg-destructive/10 text-destructive"
                      : "bg-muted text-muted-foreground"
                  }`}
                >
                  {deptEntries.length}/{MAX_ROLES_PER_DEPARTMENT}
                </span>
              </div>

              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button
                    variant="outline"
                    size="sm"
                    className="gap-1"
                    disabled={isAtLimit}
                    title={
                      isAtLimit
                        ? `Maximum ${MAX_ROLES_PER_DEPARTMENT} roles per department`
                        : undefined
                    }
                  >
                    <Plus className="h-4 w-4" />
                    Add Role
                    <ChevronDown className="h-3 w-3" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-56">
                  <DropdownMenuLabel>Common Roles</DropdownMenuLabel>
                  {libraryRoles.slice(0, 6).map((role) => (
                    <DropdownMenuItem
                      key={role}
                      onClick={() => handleAddEntry(department, role)}
                    >
                      {role}
                    </DropdownMenuItem>
                  ))}
                  {libraryRoles.length > 6 && (
                    <>
                      <DropdownMenuSeparator />
                      <DropdownMenuLabel className="text-xs text-muted-foreground">
                        More Roles
                      </DropdownMenuLabel>
                      {libraryRoles.slice(6).map((role) => (
                        <DropdownMenuItem
                          key={role}
                          onClick={() => handleAddEntry(department, role)}
                        >
                          {role}
                        </DropdownMenuItem>
                      ))}
                    </>
                  )}
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={() => handleAddEntry(department)}>
                    <Plus className="h-4 w-4 mr-2" />
                    Custom Role...
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>

            {isAtLimit && (
              <div className="flex items-center gap-2 px-4 py-2 bg-destructive/5 border-b border-destructive/20 text-sm text-destructive">
                <AlertCircle className="h-4 w-4 shrink-0" />
                Maximum {MAX_ROLES_PER_DEPARTMENT} roles reached for this
                department. Delete a role to add a new one.
              </div>
            )}

            <div className="p-4">
              {deptEntries.length > 0 && (
                <div className="grid grid-cols-[1fr,80px,140px,120px,48px] gap-3 mb-3 px-1">
                  <span className="text-sm text-muted-foreground">Role</span>
                  <span className="text-sm text-muted-foreground text-center">
                    FTEs
                  </span>
                  <span className="text-sm text-muted-foreground text-center">
                    Monthly Salary
                  </span>
                  <span className="text-sm text-muted-foreground text-right">
                    Annual Total
                  </span>
                  <span />
                </div>
              )}

              <div className="space-y-2">
                {deptEntries.map((entry) => {
                  const annualTotal = entry.totalCost * 12;
                  const isSaving = loadingIds.has(entry.id);
                  const hasError = errorIds.has(entry.id);

                  return (
                    <div
                      key={entry.id}
                      className={`grid grid-cols-[1fr,80px,140px,120px,48px] gap-3 items-center rounded-lg transition-colors ${
                        hasError ? "bg-destructive/5" : ""
                      } ${isSaving ? "opacity-60" : ""}`}
                    >
                      <div className="relative">
                        <Input
                          value={entry.role}
                          onChange={(e) =>
                            handleUpdateEntry(entry.id, "role", e.target.value)
                          }
                          onBlur={() => handleBlurEntry(entry.id)}
                          placeholder="Role name"
                          className="h-10 pr-8"
                          disabled={isSaving}
                        />
                        {isSaving && (
                          <Loader2 className="absolute right-2 top-1/2 -translate-y-1/2 h-4 w-4 animate-spin text-muted-foreground" />
                        )}
                        {hasError && !isSaving && (
                          <AlertCircle className="absolute right-2 top-1/2 -translate-y-1/2 h-4 w-4 text-destructive" />
                        )}
                      </div>

                      <Input
                        type="number"
                        value={entry.ftes}
                        onChange={(e) =>
                          handleUpdateEntry(
                            entry.id,
                            "ftes",
                            Number(e.target.value),
                          )
                        }
                        onBlur={() => handleBlurEntry(entry.id)}
                        min="0"
                        step="0.1"
                        className="h-10 text-center"
                        disabled={isSaving}
                      />

                      <div className="relative">
                        <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground text-sm">
                          €
                        </span>
                        <Input
                          type="number"
                          value={entry.salary}
                          onChange={(e) =>
                            handleUpdateEntry(
                              entry.id,
                              "salary",
                              Number(e.target.value),
                            )
                          }
                          onBlur={() => handleBlurEntry(entry.id)}
                          min="0"
                          step="100"
                          className="h-10 pl-7 text-right"
                          disabled={isSaving}
                        />
                      </div>

                      <div className="text-right pr-1">
                        <div className="font-medium text-foreground">
                          €
                          {annualTotal.toLocaleString("de-DE", {
                            maximumFractionDigits: 0,
                          })}
                        </div>
                        <div className="text-xs text-muted-foreground">
                          (€
                          {entry.totalCost.toLocaleString("de-DE", {
                            maximumFractionDigits: 0,
                          })}
                          /mo)
                        </div>
                      </div>

                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => handleDeleteEntry(entry.id)}
                        className="h-10 w-10 text-destructive hover:bg-destructive/10"
                        disabled={isSaving}
                        title="Delete role"
                      >
                        {isSaving ? (
                          <Loader2 className="h-4 w-4 animate-spin" />
                        ) : (
                          <Trash2 className="h-4 w-4" />
                        )}
                      </Button>
                    </div>
                  );
                })}
              </div>

              {deptSuggestions.length > 0 && (
                <div className="mt-4 pt-4 border-t border-dashed">
                  <div className="flex items-center gap-2 mb-3">
                    <span className="text-sm font-medium text-muted-foreground">
                      Other Roles to Consider
                    </span>
                  </div>
                  <div className="space-y-2">
                    {deptSuggestions.map((suggestion) => {
                      const isSaving = loadingIds.has(suggestion.id);
                      return (
                        <div
                          key={suggestion.id}
                          className="flex items-center justify-between p-3 rounded-lg bg-muted/50 border border-border"
                        >
                          <div className="flex-1">
                            <div className="flex items-center gap-2">
                              <span className="font-medium text-sm">
                                {suggestion.role}
                              </span>
                              <span className="text-xs text-muted-foreground">
                                ({suggestion.ftes.toFixed(1)} FTE)
                              </span>
                            </div>
                            <p className="text-xs text-muted-foreground mt-0.5">
                              {suggestion.reason}
                            </p>
                            <p className="text-xs text-muted-foreground mt-1">
                              Est. €
                              {Math.round(
                                suggestion.totalCost * 12,
                              ).toLocaleString("de-DE")}
                              /year
                            </p>
                          </div>
                          <Button
                            size="sm"
                            variant="outline"
                            className="ml-3"
                            onClick={() =>
                              handleAcceptSuggestion(suggestion.id)
                            }
                            disabled={isSaving || isAtLimit}
                            title={
                              isAtLimit
                                ? `Maximum ${MAX_ROLES_PER_DEPARTMENT} roles reached`
                                : undefined
                            }
                          >
                            {isSaving ? (
                              <Loader2 className="h-4 w-4 mr-1 animate-spin" />
                            ) : (
                              <Plus className="h-4 w-4 mr-1" />
                            )}
                            Add
                          </Button>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>

            {deptEntries.length > 0 && (
              <div className="grid grid-cols-[1fr,80px,140px,120px,48px] gap-3 items-center px-5 py-3 bg-primary/5 border-t">
                <span className="font-semibold text-foreground">
                  Department Total
                </span>
                <span className="text-center font-semibold text-foreground">
                  {totalFtes.toFixed(1)}
                </span>
                <span />
                <span className="text-right font-semibold text-foreground">
                  €
                  {totalAnnual.toLocaleString("de-DE", {
                    maximumFractionDigits: 0,
                  })}
                </span>
                <span />
              </div>
            )}
          </Card>
        );
      })}

      {entries.length === 0 && (
        <Card className="p-6">
          <p className="text-muted-foreground mb-4">
            No roles configured. Add your first role to a department:
          </p>
          <div className="flex flex-wrap gap-2">
            {DEPARTMENTS.map((dept) => (
              <DropdownMenu key={dept}>
                <DropdownMenuTrigger asChild>
                  <Button variant="outline" size="sm">
                    <Plus className="h-4 w-4 mr-1" />
                    {dept}
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent>
                  {(ROLE_LIBRARY[dept] || []).map((role) => (
                    <DropdownMenuItem
                      key={role}
                      onClick={() => handleAddEntry(dept, role)}
                    >
                      {role}
                    </DropdownMenuItem>
                  ))}
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={() => handleAddEntry(dept)}>
                    <Plus className="h-4 w-4 mr-2" />
                    Custom Role...
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            ))}
          </div>
        </Card>
      )}

      {entries.length > 0 && (
        <Card className="p-6 bg-gradient-to-br from-primary/5 to-accent/5 border-2">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div>
              <p className="text-sm text-muted-foreground mb-2">Total FTEs</p>
              <p className="text-2xl font-bold text-primary">
                {grandTotalFtes.toFixed(1)}
              </p>
            </div>
            <div>
              <p className="text-sm text-muted-foreground mb-2">
                Total Monthly Cost
              </p>
              <p className="text-2xl font-bold text-primary">
                €
                {totalMonthlyCost.toLocaleString("de-DE", {
                  maximumFractionDigits: 0,
                })}
              </p>
            </div>
            <div>
              <p className="text-sm text-muted-foreground mb-2">
                Total Annual Cost
              </p>
              <p className="text-2xl font-bold text-accent text-black">
                €
                {totalAnnualCost.toLocaleString("de-DE", {
                  maximumFractionDigits: 0,
                })}
              </p>
            </div>
          </div>
          <div className="mt-4 pt-4 border-t border-border/50">
            <div className="flex justify-between items-center">
              <div className="text-sm text-muted-foreground">
                {entries.length} positions •{" "}
                {Object.keys(entriesByDepartment).length} departments
              </div>
              <div className="text-sm text-muted-foreground">
                Average salary: €
                {entries.length > 0
                  ? Math.round(
                      entries.reduce((sum, e) => sum + e.salary, 0) /
                        entries.length,
                    ).toLocaleString()
                  : 0}
                /month • Social rate: {socialChargesPercent}%
              </div>
            </div>
          </div>
        </Card>
      )}
    </div>
  );
};

export type PayrollEntry = {
  id: string;
  department: string;
  role: string;
  ftes: number;
  salary: number; // monthly
  socialCharges: number; // percentage
  totalCost: number; // monthly
};

export type SuggestedRole = {
  id: string;
  department: string;
  role: string;
  ftes: number;
  salary: number;
  socialCharges: number;
  totalCost: number;
  reason: string;
};
